/**
 * Routage et fournisseurs.
 *
 * Une route protégée est une route dont le rendu exige une identité : sans elle, l'écran de connexion
 * s'affiche. C'est du confort d'interface, pas une sécurité — chaque appel d'API est de toute façon
 * refusé sans jeton valide (`docs/agent-spec/06-authentification-et-droits.md`).
 *
 * Le module paramétrage n'est monté que pour `admin-totalenergies` : un autre profil qui saisirait
 * directement `/parametrage` dans la barre d'adresse tombe sur le même écran "à venir" que toute
 * route inconnue, jamais sur un formulaire d'administration qu'il ne pourrait de toute façon pas
 * utiliser (le serveur refuse chaque appel avec `403`).
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes } from "react-router-dom";

import { Suspense, lazy } from "react";

import { EcranAlertes } from "../features/alertes/EcranAlertes";
import { EcranParametrage } from "../features/admin/EcranParametrage";
import { EcranConnexion } from "../features/connexion/EcranConnexion";
import { EcranDocuments } from "../features/documents/EcranDocuments";
import { EcranFicheGrv } from "../features/grv/EcranFicheGrv";
import { EcranListeGrv } from "../features/grv/EcranListeGrv";
import { EcranCategorieIndicateurs } from "../features/kpi/EcranCategorieIndicateurs";
import { EcranTableauDeBord } from "../features/kpi/EcranTableauDeBord";
import { EcranMaintenance } from "../features/maintenance/EcranMaintenance";
import { EcranMouvements } from "../features/mouvements/EcranMouvements";
import { EcranRapports } from "../features/rapports/EcranRapports";
import { EcranParcTrackers } from "../features/trackers/EcranParcTrackers";
import { ErreurApi } from "../shared/api/erreurs";
import { useAuth, useIdentite } from "../shared/auth/contexte";
import { Chargement } from "../shared/composants/Etats";
import { MiseEnPage } from "./MiseEnPage";

// `maplibre-gl` pèse à lui seul plus que le reste de l'application : le charger paresseusement
// évite d'imposer son poids à un utilisateur qui n'ouvre jamais la carte.
const EcranCarte = lazy(() =>
  import("../features/carte/EcranCarte").then((module) => ({ default: module.EcranCarte })),
);

export function creerClientDeRequetes(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Ne pas réessayer un refus ni un introuvable : la réponse ne changera pas, et une nouvelle
        // tentative ne ferait que retarder l'affichage du message. Une panne réseau, si.
        retry: (tentatives, erreur) => {
          if (erreur instanceof ErreurApi && erreur.statut < 500) return false;
          return tentatives < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

function EnAttenteDIdentite({ children }: { children: React.ReactNode }) {
  const { identite, chargement } = useAuth();
  if (chargement) return <Chargement libelle="Vérification de la session…" />;
  if (!identite) return <EcranConnexion />;
  return <>{children}</>;
}

function RouteParametrage() {
  const identite = useIdentite();
  if (identite.profil !== "admin-totalenergies") {
    return <p className="etat etat--vide">Écran à venir.</p>;
  }
  return <EcranParametrage />;
}

export function App() {
  return (
    <QueryClientProvider client={creerClientDeRequetes()}>
      <EnAttenteDIdentite>
        <Routes>
          <Route element={<MiseEnPage />}>
            <Route path="/" element={<EcranTableauDeBord />} />
            <Route
              path="/carte"
              element={
                <Suspense fallback={<Chargement libelle="Chargement de la carte…" />}>
                  <EcranCarte />
                </Suspense>
              }
            />
            <Route path="/grv" element={<EcranListeGrv />} />
            <Route path="/trackers" element={<EcranParcTrackers />} />
            <Route path="/grv/:numero/*" element={<EcranFicheGrv />} />
            <Route path="/mouvements" element={<EcranMouvements />} />
            <Route path="/maintenance" element={<EcranMaintenance />} />
            <Route path="/documents" element={<EcranDocuments />} />
            <Route path="/alertes" element={<EcranAlertes />} />
            <Route path="/rapports" element={<EcranRapports />} />
            <Route path="/kpi/:categorie" element={<EcranCategorieIndicateurs />} />
            <Route path="/tableau-de-bord" element={<EcranTableauDeBord />} />
            <Route path="/parametrage/*" element={<RouteParametrage />} />
            <Route path="*" element={<p className="etat etat--vide">Écran à venir.</p>} />
          </Route>
        </Routes>
      </EnAttenteDIdentite>
    </QueryClientProvider>
  );
}
